import {
  createEmptyIntensityZoneCounts,
  intensityZoneIdForPercent,
} from "./insightsChartHelpers";
import { accumulatePrescribedAvgRpeRow } from "./insightsPrescribedRpe";
import {
  forEachInsightsSetRow,
  INSIGHTS_DATA_SOURCE,
  countedRepsForSetRow,
  parsePositiveNumber,
} from "./insightsSetRowMath";

/**
 * @returns {{
 *   sets: number,
 *   reps: number,
 *   volumeKg: number,
 *   peakPct: number,
 *   pctSum: number,
 *   pctCount: number,
 *   zones: Record<string, number>,
 *   weightedRpeSum: number,
 *   rpeVolumeWeight: number,
 * }}
 */
export function createEmptyLiftScalars() {
  return {
    sets: 0,
    reps: 0,
    volumeKg: 0,
    peakPct: 0,
    pctSum: 0,
    pctCount: 0,
    zones: createEmptyIntensityZoneCounts(),
    weightedRpeSum: 0,
    rpeVolumeWeight: 0,
  };
}

/** Function to accumulate lift set row into scalars.
 * @param {object} row
 * @param {number} mult
 * @param {ReturnType<typeof createEmptyLiftScalars>} acc
 */
export function accumulateLiftSetRowIntoScalars(
  row,
  mult,
  acc,
  exercise,
  dataSource = INSIGHTS_DATA_SOURCE.PRESCRIBED
) {
  acc.sets += mult;

  const repVal = countedRepsForSetRow(row, exercise, dataSource);
  acc.reps += mult * repVal;
  const loadKg = parsePositiveNumber(row?.load);
  if (loadKg > 0 && repVal > 0) {
    acc.volumeKg += mult * repVal * loadKg;
  }

  const pct = parsePositiveNumber(row?.percentRepMax);
  if (pct > 0) {
    acc.peakPct = Math.max(acc.peakPct, pct);
    acc.pctSum += pct * mult;
    acc.pctCount += mult;
    const zoneId = intensityZoneIdForPercent(pct);
    if (zoneId) {
      acc.zones[zoneId] += mult;
    }
  }

  accumulatePrescribedAvgRpeRow(row, mult, exercise, acc);
}

/** Function to accumulate lift week scalars.
 * @param {object[]} exercises
 * @param {string} [dataSource]
 * @returns {ReturnType<typeof createEmptyLiftScalars>}
 */
export function accumulateLiftWeekScalars(
  exercises,
  dataSource = INSIGHTS_DATA_SOURCE.PRESCRIBED
) {
  const acc = createEmptyLiftScalars();
  for (const ex of exercises) {
    forEachInsightsSetRow(
      ex,
      (row, mult, rowExercise) => {
        accumulateLiftSetRowIntoScalars(
          row,
          mult,
          acc,
          rowExercise ?? ex,
          dataSource
        );
      },
      dataSource
    );
  }
  return acc;
}
